// src/hooks/useJanetRealtime.js — OpenAI Realtime voice for the Janet FAB.
//
// Architecture:
//   Browser mic → PCM16 24kHz → OpenAI Realtime WebSocket → GPT-Realtime-2
//   Tool calls → /api/janet-realtime-tools (Claude executes) → result back to RT
//   RT audio response → PCM16 24kHz → WebAudio playback
//
// Public API:
//   start(systemPrompt, pageCtx, preflight)  — open mic + RT session; an
//     optional `preflight` (from createVoicePreflight, below) supplies an
//     already-acquired mic stream + AudioContext instead of start()
//     acquiring its own — see createVoicePreflight's own header comment.
//   stop()               — close everything
//   status               — 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking' | 'error'
//   transcript           — { user: string, janet: string } — live transcripts
//   error                — error message if status === 'error'
//
// createVoicePreflight() / releaseVoicePreflight(preflight) — a caller-side
// gesture helper (2026-09-30 repair, issue #178): a doorway that wants to
// mount this hook's consumer and immediately call start() (e.g. Home's
// compact "Talk to Janet" dock) can call createVoicePreflight() SYNCHRONOUSLY
// inside its own click handler to acquire the mic + unlock audio before the
// gesture's transient activation expires, then hand the result into start()
// as its third argument. releaseVoicePreflight frees it if it's abandoned
// before start() ever runs (e.g. the caller collapsed/unmounted first).

import { useState, useRef, useCallback, useEffect } from 'react'
import { janetFetch } from '../lib/janetFetch'

// Tool schemas in OpenAI format (subset of Janet's full tool set for in-OS use)
const RT_TOOLS = [
  {
    type: 'function',
    name: 'search_contacts',
    description: 'Find contacts by name or email.',
    parameters: {
      type: 'object',
      properties: {
        query: { type: 'string', description: 'Name or email to search for.' },
        limit: { type: 'integer', default: 6 },
      },
      required: ['query'],
    },
  },
  {
    type: 'function',
    name: 'get_contact',
    description: 'Full detail for one contact — emails, phones, title, organization, recent activity.',
    parameters: {
      type: 'object',
      properties: {
        id: { type: 'string', description: 'Contact UUID from search_contacts.' },
      },
      required: ['id'],
    },
  },
  {
    type: 'function',
    name: 'list_tasks',
    description: "List the principal's tasks. Filter by quadrant or status.",
    parameters: {
      type: 'object',
      properties: {
        quadrant: { type: 'integer', description: '1=urgent+important, 2=important, 3=urgent, 4=neither. Omit for all.' },
        status:   { type: 'string', enum: ['pending', 'in_progress', 'done'], description: 'Omit for pending+in_progress.' },
        limit:    { type: 'integer', default: 10 },
      },
      required: [],
    },
  },
  {
    type: 'function',
    name: 'search_projects',
    description: 'Search projects and properties by name or keyword.',
    parameters: {
      type: 'object',
      properties: {
        query: { type: 'string' },
        limit: { type: 'integer', default: 6 },
      },
      required: ['query'],
    },
  },
  {
    type: 'function',
    name: 'search_deals',
    description: 'Search deals by title, contact name, or project.',
    parameters: {
      type: 'object',
      properties: {
        query: { type: 'string' },
        limit: { type: 'integer', default: 6 },
      },
      required: ['query'],
    },
  },
  {
    type: 'function',
    name: 'free_busy',
    description: "Check the principal's calendar availability for a date range.",
    parameters: {
      type: 'object',
      properties: {
        start_date: { type: 'string', description: 'YYYY-MM-DD' },
        end_date:   { type: 'string', description: 'YYYY-MM-DD' },
      },
      required: ['start_date', 'end_date'],
    },
  },
  {
    type: 'function',
    name: 'get_inbox_summary',
    description: 'Get a summary of recent important emails needing attention.',
    parameters: { type: 'object', properties: {}, required: [] },
  },
]

// PCM16 helpers — shared with useJanetInterviewVoice
export function float32ToPcm16(float32Buf) {
  const out = new Int16Array(float32Buf.length)
  for (let i = 0; i < float32Buf.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Buf[i]))
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  return out
}

export function pcm16ToFloat32(int16Buf) {
  const out = new Float32Array(int16Buf.length)
  for (let i = 0; i < int16Buf.length; i++) {
    out[i] = int16Buf[i] / 0x8000
  }
  return out
}

export function base64ToUint8(b64) {
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

export function uint8ToBase64(buf) {
  let bin = ''
  for (let i = 0; i < buf.length; i++) bin += String.fromCharCode(buf[i])
  return btoa(bin)
}

const MIC_CONSTRAINTS = {
  audio: { channelCount: 1, sampleRate: 24000, echoCancellation: true, noiseSuppression: true },
}

// Pre-acquire the mic + unlock audio SYNCHRONOUSLY, inside a caller's own
// user-gesture call stack (2026-09-30 repair, issue #178). A doorway like
// HomeJanetDockMobile's "Talk to Janet" mounts JanetChatPanel with
// startLiveVoiceOnMount, but by the time that effect's own 150ms timer, then
// handleStartLiveVoice's /api/janet-voice-prompt await, then start()'s own
// /api/janet-realtime-session await have all resolved, the tap's transient
// user activation has expired — browsers that gate getUserMedia/AudioContext
// on it (notably iOS Safari) can reject the mic request or leave the
// AudioContext suspended. Calling getUserMedia and constructing/resuming the
// AudioContext HERE — synchronously, in the tap's own call stack — and
// handing the (still in-flight) results into start() as an optional third
// argument keeps mic/audio acquisition inside the gesture without touching
// start()'s own flow, or any other caller (the header "Live voice" button
// passes no preflight and behaves exactly as before).
export function createVoicePreflight() {
  let audioContext = null
  try {
    audioContext = new AudioContext({ sampleRate: 24000 })
    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {})
    }
  } catch {
    audioContext = null
  }

  let streamPromise
  try {
    streamPromise = navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS)
  } catch (err) {
    streamPromise = Promise.reject(err)
  }
  // start() (or releaseVoicePreflight) awaits/handles this itself — swallow
  // here so an unclaimed preflight (e.g. the dock collapsed before start()
  // ever ran) never surfaces as an unhandled promise rejection.
  streamPromise.catch(() => {})

  return { streamPromise, audioContext, claimed: false, released: false }
}

// Frees a preflight's mic track + AudioContext when nobody ever claimed it —
// e.g. the dock was collapsed, or its host unmounted, before start() got a
// chance to run with it. A no-op once start() has taken ownership (claimed),
// and safe to call more than once. Sets `released` (distinct from `claimed`,
// which both this and start() use as the general already-handled guard) so
// start() can tell "torn down without ever being used" apart from "in the
// process of being used right now" if it's ever handed this preflight after
// the fact (2026-09-30 repair, issue #178 — the compact dock's Close tapped
// while handleStartLiveVoice was still awaiting the voice-prompt fetch).
export function releaseVoicePreflight(preflight) {
  if (!preflight || preflight.claimed) return
  preflight.claimed = true
  preflight.released = true
  preflight.streamPromise.then(
    (stream) => { try { stream.getTracks().forEach((t) => t.stop()) } catch {} },
    () => {},
  )
  if (preflight.audioContext) {
    try { preflight.audioContext.close() } catch {}
  }
}

export function useJanetRealtime() {
  const [status,     setStatus]     = useState('idle')    // idle|connecting|listening|thinking|speaking|error
  const [transcript, setTranscript] = useState({ user: '', janet: '' })
  const [error,      setError]      = useState(null)

  const wsRef          = useRef(null)   // WebSocket to OpenAI Realtime
  const audioCtxRef    = useRef(null)   // AudioContext for mic capture + playback
  const streamRef      = useRef(null)   // MediaStream (microphone)
  const processorRef   = useRef(null)   // ScriptProcessorNode
  const playQueueRef   = useRef([])     // queued PCM16 chunks to play
  const playingRef     = useRef(false)  // true while draining playQueueRef
  const pendingCallsRef = useRef({})    // call_id → {name, args}
  const destroyedRef   = useRef(false)
  const sessionPageRef = useRef(null)   // page context set by start()

  const cleanup = useCallback(() => {
    destroyedRef.current = true

    if (processorRef.current) {
      try { processorRef.current.disconnect() } catch {}
      processorRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close() } catch {}
      audioCtxRef.current = null
    }
    if (wsRef.current) {
      try { wsRef.current.close() } catch {}
      wsRef.current = null
    }

    playQueueRef.current  = []
    playingRef.current    = false
    pendingCallsRef.current = {}
  }, [])

  // ── Audio playback queue ─────────────────────────────────────────────────
  const drainPlayQueue = useCallback(() => {
    if (playingRef.current || !audioCtxRef.current) return
    const chunk = playQueueRef.current.shift()
    if (!chunk) { playingRef.current = false; setStatus('listening'); return }

    playingRef.current = true
    setStatus('speaking')

    const int16 = new Int16Array(chunk.buffer, chunk.byteOffset, chunk.byteLength / 2)
    const float32 = pcm16ToFloat32(int16)

    const buf = audioCtxRef.current.createBuffer(1, float32.length, 24000)
    buf.getChannelData(0).set(float32)

    const src = audioCtxRef.current.createBufferSource()
    src.buffer = buf
    src.connect(audioCtxRef.current.destination)
    src.onended = () => {
      playingRef.current = false
      drainPlayQueue()
    }
    src.start()
  }, [])

  const enqueueAudio = useCallback((base64chunk) => {
    const raw = base64ToUint8(base64chunk)
    playQueueRef.current.push(raw)
    drainPlayQueue()
  }, [drainPlayQueue])

  // ── Tool execution ───────────────────────────────────────────────────────
  const executeTool = useCallback(async (callId, name, argsStr) => {
    let args = {}
    try { args = JSON.parse(argsStr || '{}') } catch {}

    let result = 'Tool not available'
    try {
      const resp = await janetFetch('/api/janet-realtime-tools', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tool: name, args, page: sessionPageRef.current }),
      })
      const data = await resp.json()
      result = data.result || JSON.stringify(data)
    } catch (err) {
      result = `Error: ${err.message}`
    }

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return

    // Send result back to RT
    wsRef.current.send(JSON.stringify({
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output: typeof result === 'string' ? result : JSON.stringify(result),
      },
    }))

    // Ask RT to continue speaking
    wsRef.current.send(JSON.stringify({ type: 'response.create' }))
  }, [])

  // ── RT event handler ─────────────────────────────────────────────────────
  const onRTMessage = useCallback((raw) => {
    let event
    try { event = JSON.parse(raw) } catch { return }

    switch (event.type) {
      case 'session.created':
      case 'session.updated':
        setStatus('listening')
        break

      case 'input_audio_buffer.speech_started':
        // Interrupt any playing audio
        playQueueRef.current = []
        playingRef.current = false
        setStatus('listening')
        setTranscript(t => ({ ...t, janet: '' }))
        break

      case 'conversation.item.input_audio_transcription.completed':
        setTranscript(t => ({ ...t, user: event.transcript || '' }))
        setStatus('thinking')
        break

      case 'response.output_audio.delta':
      case 'response.audio.delta':
        enqueueAudio(event.delta)
        break

      case 'response.output_audio_transcript.delta':
        setTranscript(t => ({ ...t, janet: (t.janet || '') + (event.delta || '') }))
        break

      case 'response.output_audio_transcript.done':
        // Full transcript for this turn done — leave it displayed
        break

      case 'response.output_item.added':
        if (event.item?.type === 'function_call') {
          pendingCallsRef.current[event.item.call_id] = { name: event.item.name, args: '' }
        }
        break

      case 'response.function_call_arguments.delta': {
        const fc = pendingCallsRef.current[event.call_id]
        if (fc) fc.args += (event.delta || '')
        break
      }

      case 'response.function_call_arguments.done': {
        const fc = pendingCallsRef.current[event.call_id]
        if (fc) {
          delete pendingCallsRef.current[event.call_id]
          setStatus('thinking')
          executeTool(event.call_id, fc.name, event.arguments || fc.args)
        }
        break
      }

      case 'response.done':
        if (playQueueRef.current.length === 0 && !playingRef.current) {
          setStatus('listening')
        }
        setTranscript(t => ({ ...t, user: '' }))
        break

      case 'error':
        console.error('[janet-rt] error:', event.error)
        setError(event.error?.message || 'Realtime error')
        setStatus('error')
        break
    }
  }, [enqueueAudio, executeTool])

  // ── start() ──────────────────────────────────────────────────────────────
  // `preflight`, when supplied, is a { streamPromise, audioContext, claimed }
  // object from createVoicePreflight() above — mic acquisition and audio
  // unlock that already happened synchronously inside the CALLER's own
  // gesture, before this async function's first await. Every other caller
  // (the header "Live voice" button, voice interviews, etc.) passes nothing
  // and this behaves exactly as before: getUserMedia and `new AudioContext`
  // both happen inside start() itself.
  const start = useCallback(async (systemPrompt, pageCtx = null, preflight = null) => {
    // A preflight already released by its owner (e.g. the caller's dock
    // closed before anything ever claimed it) must never be used — its
    // stream track is already stopped and its AudioContext already closed.
    // Refuse outright: no realtime session may start after a close, and
    // silently falling back to acquiring a fresh mic here would start a
    // session nobody asked for anymore. This is defense-in-depth — the
    // mounted-guard in JanetChatPanel's handleStartLiveVoice is what stops
    // start() from ever being called with a released preflight in the
    // traced scenario (2026-09-30 repair, issue #178).
    if (preflight?.released) return

    destroyedRef.current = false
    sessionPageRef.current = pageCtx
    setError(null)
    setTranscript({ user: '', janet: '' })
    setStatus('connecting')

    // Ownership of a supplied preflight transfers to this call the instant
    // we decide to use it — marked synchronously, before any await, so a
    // concurrent dock-collapse/unmount cleanup (releaseVoicePreflight) can
    // never race this and double-free the same stream/context. From here
    // on, THIS function is responsible for releasing it on every early
    // return below; once assigned into streamRef/audioCtxRef further down,
    // the hook's own cleanup() (stop() or unmount) takes over as usual.
    if (preflight) preflight.claimed = true
    const releasePreflightStream = () => {
      if (!preflight) return
      preflight.streamPromise.then(
        (s) => { try { s.getTracks().forEach((t) => t.stop()) } catch {} },
        () => {},
      )
    }
    const releasePreflightContext = () => {
      if (preflight?.audioContext) { try { preflight.audioContext.close() } catch {} }
    }

    // 1. Get ephemeral token
    let token
    try {
      const r = await janetFetch('/api/janet-realtime-session', { method: 'POST' })
      if (!r.ok) throw new Error(`Session token failed (${r.status})`)
      const data = await r.json()
      token = data.client_secret?.value
      if (!token) throw new Error('No client_secret in response')
    } catch (err) {
      setError(err.message)
      setStatus('error')
      releasePreflightStream()
      releasePreflightContext()
      return
    }

    if (destroyedRef.current) {
      releasePreflightStream()
      releasePreflightContext()
      return
    }

    // 2. Open mic — reuse the pre-acquired stream when supplied; never call
    // getUserMedia a second time for the same gesture.
    let stream
    try {
      stream = preflight
        ? await preflight.streamPromise
        : await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS)
    } catch (err) {
      setError('Microphone access denied')
      setStatus('error')
      releasePreflightContext()
      return
    }

    if (destroyedRef.current) {
      stream.getTracks().forEach(t => t.stop())
      releasePreflightContext()
      return
    }
    streamRef.current = stream

    // 3. AudioContext for mic capture + playback — reuse the pre-acquired,
    // gesture-created context when supplied (it was unlocked by the tap
    // that started this call; a fresh one built here would not be).
    let ctx = preflight?.audioContext || null
    if (ctx) {
      if (ctx.state === 'suspended') { try { ctx.resume() } catch {} }
    } else {
      ctx = new AudioContext({ sampleRate: 24000 })
    }
    audioCtxRef.current = ctx

    const src = ctx.createMediaStreamSource(stream)
    // ScriptProcessor for PCM capture (4096 samples ≈ 170ms chunks at 24kHz)
    const proc = ctx.createScriptProcessor(4096, 1, 1)
    processorRef.current = proc

    proc.onaudioprocess = (e) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
      const float32 = e.inputBuffer.getChannelData(0)
      const pcm16   = float32ToPcm16(float32)
      const b64     = uint8ToBase64(new Uint8Array(pcm16.buffer))
      wsRef.current.send(JSON.stringify({
        type: 'input_audio_buffer.append',
        audio: b64,
      }))
    }

    src.connect(proc)
    proc.connect(ctx.destination)  // needed or processor won't fire in some browsers

    // 4. Open RT WebSocket
    const ws = new WebSocket(
      `wss://api.openai.com/v1/realtime?model=gpt-realtime-2`,
      ['realtime', `openai-insecure-api-key.${token}`, 'openai-beta.realtime-v1']
    )
    wsRef.current = ws

    ws.onopen = () => {
      if (destroyedRef.current) { ws.close(); return }
      ws.send(JSON.stringify({
        type: 'session.update',
        session: {
          type:           'realtime',
          modalities:     ['audio', 'text'],
          voice:          'coral',
          instructions:   systemPrompt,
          input_audio_format:  'pcm16',
          output_audio_format: 'pcm16',
          input_audio_transcription: { model: 'whisper-1' },
          turn_detection: {
            type:                'server_vad',
            threshold:           0.5,
            prefix_padding_ms:   300,
            silence_duration_ms: 500,
            create_response:     true,
          },
          tools: RT_TOOLS,
          tool_choice: 'auto',
        },
      }))
    }

    ws.onmessage = (e) => onRTMessage(e.data)

    ws.onerror = () => {
      if (!destroyedRef.current) {
        setError('Connection error')
        setStatus('error')
      }
    }

    ws.onclose = (e) => {
      if (!destroyedRef.current && e.code !== 1000) {
        setError(`Disconnected (${e.code})`)
        setStatus('error')
      } else if (!destroyedRef.current) {
        setStatus('idle')
      }
    }
  }, [onRTMessage])

  const stop = useCallback(() => {
    cleanup()
    setStatus('idle')
    setTranscript({ user: '', janet: '' })
  }, [cleanup])

  // Cleanup on unmount
  useEffect(() => () => { destroyedRef.current = true; cleanup() }, [cleanup])

  return { start, stop, status, transcript, error }
}
